import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import { MdDownload, MdShare, MdStorefront, MdQrCode2 } from "react-icons/md";
import { AWS_URL } from "../services/http.service";
import { getAbsoluteStorefrontUrl } from "../pages/online-store/storefrontPaths";
import { t } from "i18next";

const getBusinessAddress = (business = {}) => {
  const parts = [
    business?.firstLine,
    business?.address?.street,
    business?.street,
    business?.townVillageBlock,
    business?.landMark,
    business?.district,
    business?.city,
    business?.state,
    business?.pincode,
  ];

  return parts
    .filter((part) => part && part.toString().trim())
    .map((part) => part.toString().trim())
    .join(", ");
};

const normalizeQrImage = (value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${AWS_URL}${value}`;
};

const buildCombinedQrText = ({
  businessName,
  phone,
  whatsappNumber,
  address,
  onlineStoreUrl,
  paymentQrUrl,
}) => {
  const lines = [
    `${t("NEW_BUSINESS_QR.BUSINESS_NAME")}: ${businessName || "NA"}`,
    `${t("NEW_BUSINESS_QR.PHONE")}: ${phone || "NA"}`,
    `${t("NEW_BUSINESS_QR.WHATSAPP")}: ${whatsappNumber || "NA"}`,
    `${t("NEW_BUSINESS_QR.ADDRESS")}: ${address || "NA"}`,
    `${t("NEW_BUSINESS_QR.ONLINE_STORE_LINK")}: ${onlineStoreUrl || "NA"}`,
  ];

  if (paymentQrUrl) {
    lines.push(`${t("NEW_BUSINESS_QR.PAYMENT_QR")}: ${paymentQrUrl}`);
  }

  return lines.join("\n");
};

export default function BusinessDetailsQrPopup({
  isOpen,
  onClose,
  business,
  bussinessUuid,
  settings,
}) {
  const combinedQrRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const businessName = business?.businessName || "Business";
  const phone = business?.phone || "";
  const whatsappNumber =
    settings?.callAndWhatsappSettings?.whatsappNumber || phone || "";
  const address = getBusinessAddress(business);

  const onlineStoreUrl = useMemo(() => {
    if (!bussinessUuid) return "";
    return getAbsoluteStorefrontUrl({
      businessName,
      bussinessUuid,
    });
  }, [businessName, bussinessUuid]);

  const paymentQrUrl = useMemo(
    () => normalizeQrImage(business?.aadharPhoto || business?.bussinessUpiQr),
    [business?.aadharPhoto, business?.bussinessUpiQr],
  );

  const combinedQrText = useMemo(
    () =>
      buildCombinedQrText({
        businessName,
        phone,
        whatsappNumber,
        address,
        onlineStoreUrl,
        paymentQrUrl,
      }),
    [
      businessName,
      phone,
      whatsappNumber,
      address,
      onlineStoreUrl,
      paymentQrUrl,
    ],
  );

  const captureCombinedCard = async () => {
    if (!combinedQrRef.current) {
      throw new Error(t("NEW_BUSINESS_QR.QR_CARD_NOT_AVAILABLE"));
    }

    return html2canvas(combinedQrRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
  };

  const downloadCombinedQr = async () => {
    try {
      setIsDownloading(true);
      const canvas = await captureCombinedCard();
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${businessName.replace(/\s+/g, "-")}-business-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(t("NEW_BUSINESS_QR.BUSINESS_QR_DOWNLOADED"));
    } catch (error) {
      toast.error(t("NEW_BUSINESS_QR.UNABLE_TO_DOWNLOAD_QR"));
    } finally {
      setIsDownloading(false);
    }
  };

  const shareCombinedQr = async () => {
    try {
      setIsSharing(true);
      const canvas = await captureCombinedCard();

      if (navigator.share) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png"),
        );

        if (!blob) {
          throw new Error(t("NEW_BUSINESS_QR.FAILED_TO_CONVERT_QR"));
        }

        const file = new File(
          [blob],
          `${businessName.replace(/\s+/g, "-")}-business-qr.png`,
          {
            type: "image/png",
          },
        );

        await navigator.share({
          title: `${businessName} ${t("NEW_BUSINESS_QR.TITLE")}`,
          text: t("NEW_BUSINESS_QR.SCAN_QR_TO_VIEW", { businessName }),
          files: [file],
        });
      } else {
        const whatsappText = t("NEW_BUSINESS_QR.QR_TEXT_TEMPLATE", {
          businessName,
          phone: phone || "NA",
          whatsapp: whatsappNumber || "NA",
          storeUrl: onlineStoreUrl || "NA",
        });
        window.open(
          `https://wa.me/?text=${encodeURIComponent(whatsappText)}`,
          "_blank",
        );
      }

      toast.success(t("NEW_BUSINESS_QR.BUSINESS_QR_SHARED"));
    } catch (error) {
      toast.error(t("NEW_BUSINESS_QR.UNABLE_TO_SHARE_QR"));
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4'>
      <div className='bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden'>
        <div className='bg-blue-600 p-4 text-white flex items-center justify-between'>
          <div>
            <h2 className='text-2xl font-bold'>{t("NEW_BUSINESS_QR.TITLE")}</h2>
            <p className='text-blue-100 text-sm mt-1'>
              {t("NEW_BUSINESS_QR.SUBTITLE")}
            </p>
          </div>
          <button
            onClick={onClose}
            className='text-white hover:bg-white/20 p-2 rounded-full transition-colors duration-200'
            aria-label={t("NEW_BUSINESS_QR.CLOSE_POPUP")}
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        <div className='p-6 space-y-6 max-h-[75vh] overflow-y-auto'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='space-y-4'>
              <div className='rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800'>
                <h3 className='font-semibold text-lg text-gray-800 dark:text-white mb-3'>
                  {t("NEW_BUSINESS_QR.BUSINESS_DETAILS")}
                </h3>
                <div className='space-y-2 text-sm text-gray-700 dark:text-gray-200'>
                  <p>
                    <span className='font-semibold'>{t("NEW_BUSINESS_QR.BUSINESS_NAME")}:</span> {businessName}
                  </p>
                  <p>
                    <span className='font-semibold'>{t("NEW_BUSINESS_QR.PHONE")}:</span> {phone || "NA"}
                  </p>
                  <p>
                    <span className='font-semibold'>{t("NEW_BUSINESS_QR.WHATSAPP")}:</span> {whatsappNumber || "NA"}
                  </p>
                  <p>
                    <span className='font-semibold'>{t("NEW_BUSINESS_QR.ADDRESS")}:</span> {address || "NA"}
                  </p>
                  <p className='break-all'>
                    <span className='font-semibold'>{t("NEW_BUSINESS_QR.ONLINE_STORE_LINK")}:</span> {onlineStoreUrl || "NA"}
                  </p>
                </div>
              </div>

              <div className='rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800'>
                <h3 className='font-semibold text-lg text-gray-800 dark:text-white mb-3 flex items-center gap-2'>
                  <MdStorefront className='text-blue-600' /> {t("NEW_BUSINESS_QR.ONLINE_STORE_QR")}
                </h3>
                <div className='flex justify-center'>
                  {onlineStoreUrl ? (
                    <QRCodeCanvas value={onlineStoreUrl} size={170} includeMargin={true} />
                  ) : (
                    <p className='text-sm text-gray-500'>{t("NEW_BUSINESS_QR.STORE_URL_NOT_AVAILABLE")}</p>
                  )}
                </div>
              </div>

              <div className='rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800'>
                <h3 className='font-semibold text-lg text-gray-800 dark:text-white mb-3'>
                  {t("NEW_BUSINESS_QR.PAYMENT_QR")}
                </h3>
                <div className='flex justify-center'>
                  {paymentQrUrl ? (
                    <img
                      src={paymentQrUrl}
                      alt={t("NEW_BUSINESS_QR.PAYMENT_QR")}
                      className='w-44 h-44 object-contain border border-gray-200 rounded-lg bg-white'
                    />
                  ) : (
                    <p className='text-sm text-gray-500'>{t("NEW_BUSINESS_QR.NO_PAYMENT_QR")}</p>
                  )}
                </div>
              </div>
            </div>

            <div className='space-y-4'>
              <div
                ref={combinedQrRef}
                className='rounded-2xl border-2 border-blue-200 dark:border-blue-700 p-5 bg-white dark:bg-gray-900'
              >
                <h3 className='font-bold text-lg text-gray-800 dark:text-white mb-4 flex items-center gap-2'>
                  <MdQrCode2 className='text-blue-600' /> {t("NEW_BUSINESS_QR.COMBINED_BUSINESS_QR")}
                </h3>
                <div className='flex justify-center mb-4'>
                  <QRCodeCanvas
                    value={combinedQrText}
                    size={260}
                    includeMargin={true}
                    level='M'
                  />
                </div>
                <p className='text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line break-words'>
                  {combinedQrText}
                </p>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <button
                  onClick={downloadCombinedQr}
                  disabled={isDownloading}
                  className='flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors'
                >
                  <MdDownload size={18} />
                  {isDownloading ? t("NEW_BUSINESS_QR.DOWNLOADING") : t("NEW_BUSINESS_QR.DOWNLOAD_COMBINED_QR")}
                </button>
                <button
                  onClick={shareCombinedQr}
                  disabled={isSharing}
                  className='flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors'
                >
                  <MdShare size={18} />
                  {isSharing ? t("NEW_BUSINESS_QR.SHARING") : t("NEW_BUSINESS_QR.SHARE_COMBINED_QR")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end'>
          <button
            onClick={onClose}
            className='px-5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
          >
            {t("NEW_BUSINESS_QR.CLOSE_POPUP")}
          </button>
        </div>
      </div>
    </div>
  );
}